import undoable from 'redux-undo'
import { ActionTypes } from './constants'
import type { QuoteActions, QuoteState } from './types'

const init: QuoteState = {
  projectSummary: [],
  companyData: {
    logo: '',
    id: '',
    name: 'company name',
    address: 'address',
    phone: 'phone',
    email: 'email',
    website: 'website',
  },
  notes: [],
  extendedNotes: '',
  quoteData: {
    quoteNumber: '',
    quoteDate: '',
    expiryDate: '',
    ownerId: '',
  },
  clientData: {
    id: null,
    preparedFor: '',
    address: '',
    city: '',
    phone: '',
    email: '',
  },
  discount: {
    value: 0,
    sign: '%',
  },
  disabledTableOptions: [],
  tax: 13,
  edited: false,
}

const quote = (state = init, action: QuoteActions): QuoteState => {
  switch (action.type) {
    case ActionTypes.UPDATE_PROPOSAL_SUCCESS:
      return {
        ...state,
        edited: false,
      }

    case ActionTypes.UPDATE_PROPOSAL_ERROR:
      return {
        ...state,
        edited: false,
      }

    case ActionTypes.SET_PROJECT_SUMMARY:
      return {
        ...state,
        projectSummary: action.payload.data,
        edited: false,
      }

    case ActionTypes.UNDO:
      return {
        ...state,
        edited: true,
      }

    case ActionTypes.REDO:
      return {
        ...state,
        edited: true,
      }

    case ActionTypes.EDIT_TABLE_CELL:
      return {
        ...state,
        edited: true,

        projectSummary: state.projectSummary.map((group, groupIndex) => {
          if (groupIndex === action.payload.groupIndex) {
            return {
              ...group,
              groupItems: group.groupItems.map((item, itemIndex) => {
                if (itemIndex === action.payload.itemIndex) {
                  return action.payload.item
                }
                return item
              }),
            }
          }
          return group
        }),
      }
    case ActionTypes.SET_COMPANY_DATA:
      return {
        ...state,
        companyData: action.payload.data,
        edited: false,
      }
    case ActionTypes.SET_CLIENT_DATA:
      return {
        ...state,
        clientData: action.payload.data,
        edited: true,
      }
    case ActionTypes.ADD_GROUP:
      return {
        ...state,
        projectSummary: [
          ...state.projectSummary,
          {
            name: action.payload.name,
            groupItems: [],
          },
        ],
      }
    case ActionTypes.ADD_ITEM:
      return {
        ...state,
        edited: true,
        projectSummary: state.projectSummary.map((group, index) => {
          if (index === action.payload.groupIndex) {
            return {
              ...group,
              groupItems: [...group.groupItems, action.payload.item],
            }
          }
          return group
        }),
      }
    case ActionTypes.REMOVE_ITEM:
      return {
        ...state,
        edited: true,
        projectSummary: state.projectSummary.map((group, index) => {
          if (index === action.payload.groupIndex) {
            return {
              ...group,
              groupItems: group.groupItems.filter((_, itemIndex) => itemIndex !== action.payload.itemIndex),
            }
          }
          return group
        }),
      }
    case ActionTypes.ADD_UNSORTED_GROUP:
      return {
        ...state,
        edited: true,

        projectSummary: [
          ...state.projectSummary,
          {
            name: 'Uncategorized',
            groupItems: action.payload.item ? [action.payload.item] : [],
          },
        ],
      }
    case ActionTypes.SET_QUOTE_DATA:
      return {
        ...state,
        edited: true,
        quoteData: {
          quoteNumber: action.payload.quoteNumber,
          quoteDate: action.payload.quoteDate,
          expiryDate: action.payload.expiryDate,
        },
      }
    case ActionTypes.SET_DISABLED_TABLE_OPTIONS:
      return {
        ...state,
        disabledTableOptions: action.payload.options,
      }
    case ActionTypes.SET_COMPANY_DATA_FIELD:
      return {
        ...state,
        companyData: {
          ...state.companyData,
          [action.payload.field]: action.payload.val,
        },
        edited: true,
      }
    case ActionTypes.SET_EDITED:
      return {
        ...state,
        edited: action.payload.edited,
      }
    case ActionTypes.SET_CLIENT_DATA_FIELD:
      return {
        ...state,
        clientData: {
          ...state.clientData,
          [action.payload.field]: action.payload.val,
        },
        edited: true,
      }
    case ActionTypes.SET_GROUP_NAME:
      return {
        ...state,
        projectSummary: state.projectSummary.map((group, index) => {
          if (index === action.payload.index) {
            return {
              ...group,
              name: action.payload.name,
            }
          }
          return group
        }),
      }
    case ActionTypes.DELETE_GROUP:
      return {
        ...state,
        edited: true,
        projectSummary: (() => {
          const groupToDelete = state.projectSummary.find((g) => g.name === action.payload.groupName)
          if (!groupToDelete) return state.projectSummary

          // If deleting Uncategorized with no items, just remove it
          if (action.payload.groupName === 'Uncategorized' && groupToDelete.groupItems.length === 0) {
            return state.projectSummary.filter((g) => g.name !== 'Uncategorized')
          }

          // Find or create Uncategorized group
          let uncategorizedGroup = state.projectSummary.find((g) => g.name === 'Uncategorized')
          if (!uncategorizedGroup) {
            uncategorizedGroup = { name: 'Uncategorized', groupItems: [] }
          }

          // Move items to Uncategorized group
          if (groupToDelete.groupItems.length > 0) {
            uncategorizedGroup = {
              ...uncategorizedGroup,
              groupItems: [...uncategorizedGroup.groupItems, ...groupToDelete.groupItems],
            }
          }

          // Remove the deleted group and old Uncategorized group, then add new Uncategorized group
          const filteredGroups = state.projectSummary.filter(
            (g) => g.name !== action.payload.groupName && g.name !== 'Uncategorized',
          )

          return [...filteredGroups, uncategorizedGroup]
        })(),
      }
    case ActionTypes.SET_DISCOUNT:
      return {
        ...state,
        discount: {
          value: action.payload.value,
          sign: action.payload.sign,
        },
        edited: true,
      }
    case ActionTypes.SET_TAX:
      return {
        ...state,
        tax: action.payload.tax,
        edited: true,
      }

    case ActionTypes.SET_NOTES:
      return {
        ...state,
        notes: action.payload.notes,
        edited: true,
      }

    case ActionTypes.SET_EXTENDED_NOTES:
      return {
        ...state,
        extendedNotes: action.payload.extendedNotes,
        edited: true,
      }

    case ActionTypes.ADD_GROUP_IMAGE:
      return {
        ...state,
        projectSummary: state.projectSummary.map((group, idx) =>
          idx === action.payload.groupIndex
            ? {
                ...group,
                images: [...(group.images || []), action.payload.imageUrl],
              }
            : group,
        ),
        edited: true,
      }

    case ActionTypes.REMOVE_GROUP_IMAGE:
      return {
        ...state,
        projectSummary: state.projectSummary.map((group, idx) =>
          idx === action.payload.groupIndex
            ? {
                ...group,
                images: group.images?.filter((_, i) => i !== action.payload.imageIndex) || [],
              }
            : group,
        ),
        edited: true,
      }

    //new reducers
    case ActionTypes.PROPOSAL_START_SUCCESS:
      return {
        ...state,
        quoteData: {
          ...state.quoteData,
          proposalId: action.payload.proposalId,
          projectId: action.payload.projectId,
        },
      }

    case ActionTypes.PROPOSAL_GENERATE_SUCCESS:
      return {
        ...state,
        projectSummary: action.payload.data.takeoffSummaries.map((summary: any) => ({
          name: summary.name,
          groupItems: summary.lineItems.map((item: any) => ({
            name: item.name,
            unitCost: item.unitCost,
            totalUnits: item.totalUnits,
            unit: item.unit,
            total: item.total,
            markUpPercentage: item.markupPercentage,
            markupSign: item.markupSign,
            profit: item.profit,
            unitPrice: item.unitPrice,
            groupPrice: item.groupPrice,
            subTotal: item.subTotal,
            totalWithMarkUp: item.totalWithMarkUp,
          })),
        })),
        clientData: action.payload.data.client
          ? {
              preparedFor: action.payload.data.client.name || '',
              address: action.payload.data.client.address || '',
              city: action.payload.data.client.city || '',
              phone: action.payload.data.client.phone || '',
              email: action.payload.data.client.email || '',
            }
          : state.clientData,
        notes: action.payload.data.notes ? [action.payload.data.notes] : [],
        quoteData: {
          ...state.quoteData,
          quoteNumber: action.payload.data.orderNumber?.toString() || '',
          expiryDate: action.payload.data.expiryDate || '',
        },
        edited: false,
      }

    case ActionTypes.SET_OWNER_ID:
      return {
        ...state,
        quoteData: {
          ...state.quoteData,
          ownerId: action.payload.ownerId,
        },
      }

    default:
      return state
  }
}

const undoableQuote = undoable(quote, {
  ignoreInitialState: true,
  syncFilter: true,
  undoType: ActionTypes.UNDO,
  redoType: ActionTypes.REDO,
  neverSkipReducer: true,
})
export default undoableQuote
